// @auctisync/api-client — the framework-neutral typed data + auth client shared
// by the website and the native apps. Construction takes injected config +
// storage (no env, no window), so the same client runs on web and React Native.
//
// Bidding is exposed ONLY as `apiClient.placeBid(...)` → the auction engine or the
// `place_bid` RPC, whichever book the sale is in (bid-route.ts). There is no
// other exported path to the `bids` ledger (CI-enforced).

import { type BidResult, type PlaceBidInput, placeBid } from './bidding.js';
import {
  type AuctisyncClientConfig,
  type AuctisyncSupabase,
  DEFAULT_REQUEST_TIMEOUT_MS,
  createSupabaseClient,
  withTimeout,
} from './client.js';
import {
  type LiveActivityTokenResult,
  type RegisterLiveActivityTokenInput,
  type RetireLiveActivityTokenInput,
  registerLiveActivityToken,
  retireLiveActivityToken,
} from './live-activity.js';

export * from './storage.js';
export * from './client.js';
export * from './refresh-on-401.js';
export * from './session-request.js';
export * from './auth.js';
export * from './bidding.js';
export * from './dashboard.js';
export * from './live-activity.js';
export * from './native-push.js';
export * from './bid-history.js';
export * from './engine-history.js';
export * from './houses.js';
export * from './live-room-layout.js';
export * from './live-room-narrow.js';
export * from './lots.js';
export * from './lot-photos.js';
export * from './photo-editing.js';
export * from './photo-edit-description.js';
export * from './lot-facts.js';
export * from './listing-prices.js';
export * from './lot-questions.js';
export * from './lot-alerts.js';
export * from './preauth.js';
export * from './connection.js';
export * from './bid-reconciliation.js';
export * from './bid-outcome.js';
export * from './registration.js';
export * from './lot-search.js';
export * from './sales.js';
export * from './sale-standings.js';
export * from './saved-searches.js';
export * from './preferences.js';
export * from './watchlist.js';
export * from './cataloging.js';
export * from './lot-drafts.js';
export * from './request-error.js';
export * from './invoices.js';
export * from './payments.js';
export * from './help.js';
export * from './house-contact.js';
export * from './pickup.js';
export * from './diagnostic-reports.js';
// The engine's live feed, protocol 2 (2026-10-08): where a sale's live updates
// come from, the feed itself, the one row every live page renders, the sound
// rule and the countdown clock — one implementation for the website and apps.
export * from './live-source.js';
export * from './engine-feed.js';
export * from './live-lot.js';
export * from './lot-sound.js';
export * from './live-clock.js';
// What a live PAGE needs between the feed and the shapes it draws that are not
// a `LiveLot` (2026-10-08, website build): the engine's figures over a row,
// the trail and the ticker from engine messages, which change was mine.
export * from './live-overlay.js';

/** The shared AuctiSync client: the typed Supabase client plus sanctioned wrappers. */
export interface AuctisyncClient {
  /** The underlying typed Supabase client, for all non-bid operations. */
  supabase: AuctisyncSupabase;
  /** The ONLY sanctioned way to place a bid (routes through the `place_bid` RPC). */
  placeBid(input: PlaceBidInput): Promise<BidResult>;
  /** Register ActivityKit's per-Activity push token so bid updates can reach it (P1.3). */
  registerLiveActivityToken(
    input: RegisterLiveActivityTokenInput,
  ): Promise<LiveActivityTokenResult>;
  /** Soft-delete an Activity's token once it is no longer on screen. */
  retireLiveActivityToken(input: RetireLiveActivityTokenInput): Promise<LiveActivityTokenResult>;
}

/**
 * Build the shared client from injected config. One call site per platform:
 * the web app passes `{ detectSessionInUrl: true }` + its localStorage adapter;
 * a native app passes `false` + a SecureStore adapter.
 */
export function createAuctisyncClient(config: AuctisyncClientConfig): AuctisyncClient {
  const supabase = createSupabaseClient(config);
  return {
    supabase,
    // The engine is reached with the same timeout and connection monitor as
    // every request through this client.
    placeBid: (input) =>
      placeBid(supabase, input, {
        fetch: withTimeout(
          config.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS,
          config.connection,
        ),
      }),
    registerLiveActivityToken: (input) => registerLiveActivityToken(supabase, input),
    retireLiveActivityToken: (input) => retireLiveActivityToken(supabase, input),
  };
}
